import { Box, Button, Typography, Card, CardContent } from "@mui/material";
import React from "react";
import "./FirstStageConfirm.css";
import "react-international-phone/style.css";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

const FirstStageConfirmTemplate = ({ application }) => {
  const accountCode = application?.loginData?.accountCode;

  const handleCopyCode = () => {
    if (accountCode) {
      navigator.clipboard.writeText(accountCode);
    }
  };

  return (
    <Box
      className="confirmation-container"
      sx={{
        width: "100%",
        padding: { xs: "50px 20px", sm: "50px 60px", lg: "50px 120px" },
        maxWidth: "1200px",
        margin: "0 auto",
        position: "relative",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          marginBottom: "20px",
        }}
      >
        <Box
          className="success-icon-wrapper"
          sx={{
            position: "relative",
            marginBottom: "20px",
          }}
        >
          <CheckCircleOutlineIcon className="successIcon" />
          <Box
            className="success-pulse"
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: "200px",
              height: "200px",
              borderRadius: "50%",
              backgroundColor: "rgba(73, 170, 25, 0.1)",
              animation: "pulse 2s ease-out infinite",
            }}
          />
        </Box>
        <Typography
          variant="h1"
          className="confirmation-title"
          sx={{
            fontSize: { xs: "32px", sm: "42px", md: "48px" },
            fontWeight: "700",
            color: "primary.darker",
            marginBottom: "16px",
            letterSpacing: "-0.5px",
          }}
        >
          Thank you for your registration!
        </Typography>
        <Box
          sx={{
            height: "4px",
            width: { xs: "120px", md: "180px" },
            background:
              "linear-gradient(90deg, transparent, #830d0b, transparent)",
            margin: "0 auto 24px auto",
            borderRadius: "2px",
            boxShadow: "0 2px 8px rgba(131, 13, 11, 0.3)",
          }}
        />
        <Typography
          sx={{
            fontSize: { xs: "15px", sm: "17px", md: "18px" },
            fontWeight: "400",
            color: "rgba(0, 0, 0, 0.7)",
            maxWidth: "700px",
            lineHeight: 1.4,
          }}
        >
          You've successfully registered in our system, and we're excited to
          have you on board. We're here to support you as you take the next
          steps, so don't hesitate to reach out if you need any help. Welcome
          aboard!
        </Typography>
      </Box>

      <Card
        className="account-code-card"
        sx={{
          maxWidth: "600px",
          margin: "0 auto 20px auto",
          borderRadius: "24px",
          boxShadow: "0 8px 32px rgba(131, 13, 11, 0.15)",
          border: "2px solid rgba(131, 13, 11, 0.1)",
          backgroundColor: "secondary.main",
          overflow: "visible",
        }}
      >
        <CardContent
          sx={{
            padding: { xs: "24px 24px" },
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontSize: { xs: "16px", sm: "18px" },
              fontWeight: "500",
              color: "rgba(0, 0, 0, 0.7)",
              marginBottom: "16px",
              textAlign: "center",
            }}
          >
            Your Account Code
          </Typography>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "12px",
              flexWrap: "wrap",
              marginBottom: "20px",
            }}
          >
            <Box
              className="account-code-box"
              sx={{
                backgroundColor: "rgba(131, 13, 11, 0.05)",
                border: "2px solid",
                borderColor: "primary.main",
                borderRadius: "12px",
                padding: { xs: "16px 24px", sm: "20px 32px" },
                minWidth: { xs: "200px", sm: "250px" },
              }}
            >
              <Typography
                variant="h4"
                sx={{
                  fontSize: { xs: "24px", sm: "28px", md: "32px" },
                  fontWeight: "700",
                  color: "primary.main",
                  fontFamily: "monospace",
                  letterSpacing: "2px",
                }}
              >
                {accountCode || "---"}
              </Typography>
            </Box>
            {accountCode && (
              <Button
                variant="outlined"
                color="primary"
                onClick={handleCopyCode}
                sx={{
                  minWidth: "auto",
                  padding: "12px",
                  borderRadius: "12px",
                  borderWidth: "2px",
                  "&:hover": {
                    borderWidth: "2px",
                    backgroundColor: "rgba(131, 13, 11, 0.05)",
                  },
                }}
                title="Copy Account Code"
              >
                <ContentCopyIcon />
              </Button>
            )}
          </Box>
          <Box
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: "center",
            }}
          >
            <Button
              variant="contained"
              color="primary"
              href="https://registration.international-robotchallenge.org/login"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                minWidth: { xs: "200px", sm: "240px" },
                padding: { xs: "14px 32px" },
                fontSize: { xs: "15px", sm: "17px" },
                fontWeight: "600",
                borderRadius: "30px",
                textTransform: "none",
                letterSpacing: "0.5px",
                boxShadow: "0 4px 16px rgba(131, 13, 11, 0.3)",
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: "0 6px 24px rgba(131, 13, 11, 0.4)",
                },
              }}
            >
              Log in to your account
            </Button>
          </Box>
        </CardContent>
      </Card>

      <Card
        className="instructions-card"
        sx={{
          maxWidth: "1200px",
          margin: "0 auto",
          borderRadius: "24px",
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.08)",
          border: "1px solid rgba(131, 13, 11, 0.1)",
          backgroundColor: "secondary.main",
        }}
      >
        <CardContent
          sx={{
            padding: { xs: "32px 24px", sm: "40px 32px", md: "48px 40px" },
          }}
        >
          <Typography
            variant="h3"
            sx={{
              fontSize: { xs: "22px", sm: "26px", md: "28px" },
              fontWeight: "700",
              color: "primary.darker",
              marginBottom: "24px",
              textAlign: "center",
              paddingBottom: "16px",
              borderBottom: "2px solid rgba(131, 13, 11, 0.1)",
            }}
          >
            What to Do Next
          </Typography>
          <Box
            component="ol"
            className="instructions-list"
            sx={{
              paddingLeft: { xs: "20px", sm: "24px" },
              marginBottom: "24px",
              "& li": {
                fontSize: { xs: "15px", sm: "17px" },
                lineHeight: 1.8,
                marginBottom: "6px",
                color: "rgba(0, 0, 0, 0.8)",
                "& span": {
                  fontWeight: "600",
                  color: "primary.main",
                },
              },
            }}
          >
            <li>
              Click the <span>Log in to your account</span> button above.
            </li>
            <li>
              Use your <span>Account Code</span> and the password you created
              during registration to log in.
            </li>
            <li>
              Complete registration by adding your teams, members, and coaches,
              and complete the payment process to finalize your registration.
            </li>
          </Box>
          <Box
            sx={{
              backgroundColor: "rgba(131, 13, 11, 0.05)",
              borderRadius: "12px",
              padding: { xs: "16px", sm: "20px" },
              borderLeft: "4px solid",
              borderColor: "primary.main",
            }}
          >
            <Typography
              sx={{
                fontSize: { xs: "14px", sm: "15px" },
                color: "rgba(0, 0, 0, 0.7)",
                lineHeight: 1.7,
                fontStyle: "italic",
              }}
            >
              <strong>Important:</strong> Make sure to save your account details
              securely! You'll need them to log in and access your account
              later.
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default FirstStageConfirmTemplate;
